import Const from '../const';

export default class SliderSnap {
    constructor(container, page) {
        this.container = container;
        this.items = [];
        this.leftControl = document.createElement('div');
        this.rightControl = document.createElement('div');
        this.slidePosition = null;
        this.currentIndex = null;
        this.page = page;

        // New properties for draggable functionality
        this.currentTranslateX = 0;
        this.itemElementWidth = 0;
        this.numItemsToSlide = 1;
        this.numVisibleItems = 4;

        // Dragging state
        this.isDragging = false;
        this.startX = 0;
        this.dragStartTranslateX = 0;
        this.hasStartedDrag = false;

        // Animation state
        this.isAnimating = false;
        this.animationDuration = 500;

        // Bind functions to keep the same reference
        this.boundSlideRight = this.slideRight.bind(this);
        this.boundSlideLeft = this.slideLeft.bind(this);
        
        // New bound functions for draggable functionality
        this.boundHandleDragStart = this.handleDragStart.bind(this);
        this.boundHandleDragMove = this.handleDragMove.bind(this);
        this.boundHandleDragEnd = this.handleDragEnd.bind(this);
        this.boundHandleWheel = this.handleWheel.bind(this);
        this.boundHandleLinkClick = this.handleLinkClick.bind(this);
    }

    sliderReInit() {
        if (this.items) {
            this.rightControl.removeEventListener('click', this.slideRight);
            this.leftControl.removeEventListener('click', this.slideLeft);

            this.sliderInit(this.items);
        }
    }

    sliderInit(items) {
        this.items = items;

        this.leftControl.classList.add(Const.widgetLeftButtonClass, Const.widgetHiddenClass);
        this.rightControl.classList.add(Const.widgetRightButtonClass);

        this.feedItems = this.container.querySelector('.garderobo-widget-feed-items');

        // Check if draggable mode is enabled
        const isDraggableMode = this.page.getWidgetParam('slider_draggable') && this.container.classList.contains('garderobo-draggable-slider');

        if (isDraggableMode) {
            // Draggable mode initialization
            if (!this.feedItems) {
                console.error('SliderSnap: feedItems container not found.');
                return;
            }

            // Add mousedown listener for dragging
            this.feedItems.removeEventListener('mousedown', this.boundHandleDragStart);
            this.feedItems.addEventListener('mousedown', this.boundHandleDragStart);
            this.feedItems.style.cursor = 'grab';

            // Add wheel listener for mousewheel/trackpad scrolling
            this.feedItems.removeEventListener('wheel', this.boundHandleWheel);
            this.feedItems.addEventListener('wheel', this.boundHandleWheel, { passive: false });

            // Add click listeners to all links within feed items
            this.addLinkClickListeners();

            // Calculate item dimensions for draggable mode
            this.itemElementWidth = 0;
            this.gap = 0;

            const firstItem = this.feedItems.querySelector('.garderobo-widget-feed-item');
            if (firstItem) {
                this.itemElementWidth = firstItem.offsetWidth;
                const computedStyle = window.getComputedStyle(this.feedItems);
                this.gap = parseFloat(computedStyle.gap) || 0;

                if (this.itemElementWidth > 0) {
                    const containerWidth = this.feedItems.offsetWidth;
                    const itemFullWidth = this.itemElementWidth + this.gap;
                    if (itemFullWidth > 0 && containerWidth > 0) {
                        const theoreticalItemsFit = containerWidth / itemFullWidth;
                        const tolerance = 0.2;
                        let calculatedVisibleItems;
                        
                        if (theoreticalItemsFit - Math.floor(theoreticalItemsFit) > (1 - tolerance)) {
                            calculatedVisibleItems = Math.ceil(theoreticalItemsFit);
                        } else {
                            calculatedVisibleItems = Math.floor(theoreticalItemsFit);
                        }
                        
                        this.numVisibleItems = Math.max(1, calculatedVisibleItems);

                        if (this.page.getWidgetParam('slider_page')) {
                            const sliderPage = this.page.getWidgetParam('slider_page');
                            this.numItemsToSlide = Number(sliderPage);
                        } else {
                            this.numItemsToSlide = this.numVisibleItems;
                        }
                    }
                }
            }

            // Initialize draggable mode positioning
            this.currentIndex = this.currentIndex || 0;
            this.currentTranslateX = 0;
            this.feedItems.style.transform = `translateX(${this.currentTranslateX}px)`;
            this.feedItems.style.transition = 'transform 0.5s ease-in-out';

            // Original scroll-based mode initialization
            this.currentPosition = this.slidePosition || 0;

            this.feedItems.scrollTo({
                left: this.currentPosition,
                behavior: 'smooth',
            });
        } else {
            // Original scroll-based mode initialization
            this.currentPosition = this.slidePosition || 0;

            this.feedItems.scrollTo({
                left: this.currentPosition,
                behavior: 'smooth',
            });

            const computedStyle = window.getComputedStyle(this.feedItems);
            this.gap = parseFloat(computedStyle.gap);

            // Clear any existing scroll timeout
            if (this.scrollTimeout) {
                clearTimeout(this.scrollTimeout);
                this.scrollTimeout = null;
            }

            // Variable to track if we're currently scrolling
            this.isScrolling = false;

            this.feedItems.addEventListener('scroll', () => {
                this.checkControlsVisibility();
                this.currentPosition = this.feedItems.scrollLeft;
                this.updateCurrentIndex();

                // For Safari fallback: mark that we're scrolling
                this.isScrolling = true;

                // Clear the existing timeout
                if (this.scrollTimeout) {
                    clearTimeout(this.scrollTimeout);
                }

                // Set a timeout to detect when scrolling stops
                this.scrollTimeout = setTimeout(() => {
                    if (this.isScrolling) {
                        this.isScrolling = false;
                        this.handleScrollEnd();
                    }
                }, 150);
            });

            // Check if scrollend is supported (Chrome and other modern browsers)
            if ('onscrollend' in window) {
                this.feedItems.addEventListener('scrollend', () => {
                    this.handleScrollEnd();
                });
            }
        }

        this.rightControl.removeEventListener('click', this.boundSlideRight);
        this.leftControl.removeEventListener('click', this.boundSlideLeft);

        this.rightControl.addEventListener('click', this.boundSlideRight);
        this.leftControl.addEventListener('click', this.boundSlideLeft);

        this.checkControlsVisibility();

        const counterEl = this.container.parentElement.querySelector('.' + Const.widgetFeedItemsCounterClass);
        if (counterEl) {
            this.counterEl = counterEl;
            this.counterBtnPrev = counterEl.querySelector('.' + Const.widgetFeedItemsCounterBtnPrevClass);
            this.counterTextCurr = counterEl.querySelector('.' + Const.widgetFeedItemsCounterTextCurrentClass);
            this.counterTextTotal = counterEl.querySelector('.' + Const.widgetFeedItemsCounterTextTotalClass);
            this.counterBtnNext = counterEl.querySelector('.' + Const.widgetFeedItemsCounterBtnNextClass);

            if (isDraggableMode) {
                this.counterTextCurr.innerHTML = this.items.length > 0 ? '1' : '0';
                this.counterTextTotal.innerHTML = this.items.length > 0 ? Math.ceil(this.items.length / this.numItemsToSlide).toString() : '0';
            } else {
                this.counterTextCurr.innerHTML = 1;
                this.counterTextTotal.innerHTML = this.items.length;
            }

            if (this.items.length === 4) {
                this.counterEl.classList.add('showItems4');
            } else {
                this.counterEl.classList.remove('showItems4');
            }

            if (this.items.length === 3) {
                this.counterEl.classList.add('showItems3');
            } else {
                this.counterEl.classList.remove('showItems3');
            }

            if (this.items.length === 2) {
                this.counterEl.classList.add('showItems2');
            } else {
                this.counterEl.classList.remove('showItems2');
            }

            this.counterBtnPrev.removeEventListener('click', this.boundSlideLeft);
            this.counterBtnNext.removeEventListener('click', this.boundSlideRight);
            this.counterBtnPrev.addEventListener('click', this.boundSlideLeft);
            this.counterBtnNext.addEventListener('click', this.boundSlideRight);
        }
    }

    updateCurrentIndex() {
        let itemWidth = this.feedItems.querySelector('.garderobo-widget-feed-item').offsetWidth;
        this.currentIndex = Math.round(this.feedItems.scrollLeft / (itemWidth + this.gap));
    }

    checkControlsVisibility() {
        const isDraggableMode = this.page.getWidgetParam('slider_draggable') && this.container.classList.contains('garderobo-draggable-slider');

        if (isDraggableMode) {
            // Draggable mode visibility logic
            if (!this.items || this.items.length === 0 || this.itemElementWidth === 0) {
                this.leftControl.classList.add(Const.widgetHiddenClass);
                this.rightControl.classList.add(Const.widgetHiddenClass);
                if (this.counterBtnPrev) this.counterBtnPrev.disabled = true;
                if (this.counterBtnNext) this.counterBtnNext.disabled = true;
                return;
            }

            this.leftControl.classList.toggle(Const.widgetHiddenClass, this.currentIndex === 0);

            const maxPossibleStartIndex = Math.max(0, this.items.length - this.numVisibleItems);
            this.rightControl.classList.toggle(Const.widgetHiddenClass, this.currentIndex >= maxPossibleStartIndex && this.items.length > 0);

            if (this.counterBtnPrev && this.counterBtnNext) {
                this.counterBtnPrev.disabled = (this.currentIndex === 0);
                this.counterBtnNext.disabled = (this.currentIndex >= maxPossibleStartIndex && this.items.length > 0);
            }
        } else {
            // Original scroll-based visibility logic
            let maxScroll = this.feedItems.scrollWidth - this.feedItems.offsetWidth;

            if (this.feedItems.scrollLeft === 0) {
                this.leftControl.classList.add(Const.widgetHiddenClass);
            } else {
                this.leftControl.classList.remove(Const.widgetHiddenClass);
            }

            if ((Math.ceil(this.feedItems.scrollLeft) >= maxScroll) || (Math.ceil(this.feedItems.scrollLeft) >= maxScroll - 1)) {
                this.rightControl.classList.add(Const.widgetHiddenClass);
            } else {
                this.rightControl.classList.remove(Const.widgetHiddenClass);
            }
        }
    }

    calculateSlideWidth() {
        let itemWidth = this.feedItems.querySelector('.garderobo-widget-feed-item').offsetWidth;
        let visibleItems = Math.floor(this.feedItems.offsetWidth / (itemWidth));

        return visibleItems * (itemWidth + this.gap);
    }

    slideRight() {
        const isDraggableMode = this.page.getWidgetParam('slider_draggable') && this.container.classList.contains('garderobo-draggable-slider');

        if (isDraggableMode) {
            // Draggable mode slide right
            if (this.isAnimating) return;
            if (this.itemElementWidth === 0 || this.items.length === 0) return;

            const itemFullWidth = this.itemElementWidth + this.gap;
            const maxAllowedStartIndex = Math.max(0, this.items.length - this.numVisibleItems);

            let targetItemIndex = this.currentIndex + this.numItemsToSlide;
            targetItemIndex = Math.min(targetItemIndex, maxAllowedStartIndex);

            if (targetItemIndex > this.currentIndex) {
                this.currentIndex = targetItemIndex;
                this.currentTranslateX = -this.currentIndex * itemFullWidth;
                this.isAnimating = true;
                this.feedItems.style.transform = `translateX(${this.currentTranslateX}px)`;
                setTimeout(() => { this.isAnimating = false; }, this.animationDuration);

                if (this.counterTextCurr && this.items.length > 0) {
                    this.counterTextCurr.innerHTML = Math.floor(this.currentIndex / this.numItemsToSlide) + 1;
                }
                this.checkControlsVisibility();
            }
        } else {
            // Original scroll-based slide right
            let maxScroll = this.feedItems.scrollWidth - this.feedItems.offsetWidth;
            let slideWidth = this.calculateSlideWidth();

            this.currentPosition = Math.min(this.currentPosition + slideWidth, maxScroll);

            this.feedItems.scrollTo({
                left: this.currentPosition,
                behavior: 'smooth',
            });

            this.updateCurrentIndex();
        }
    }

    slideLeft() {
        const isDraggableMode = this.page.getWidgetParam('slider_draggable') && this.container.classList.contains('garderobo-draggable-slider');

        if (isDraggableMode) {
            // Draggable mode slide left
            if (this.isAnimating) return;
            if (this.itemElementWidth === 0 || this.currentIndex === 0 || this.items.length === 0) return;

            const itemFullWidth = this.itemElementWidth + this.gap;
            let targetItemIndex = this.currentIndex - this.numItemsToSlide;
            targetItemIndex = Math.max(0, targetItemIndex);

            if (targetItemIndex < this.currentIndex) {
                this.currentIndex = targetItemIndex;
                this.currentTranslateX = -this.currentIndex * itemFullWidth;
                this.isAnimating = true;
                this.feedItems.style.transform = `translateX(${this.currentTranslateX}px)`;
                setTimeout(() => { this.isAnimating = false; }, this.animationDuration);

                if (this.counterTextCurr && this.items.length > 0) {
                    this.counterTextCurr.innerHTML = Math.floor(this.currentIndex / this.numItemsToSlide) + 1;
                }
                this.checkControlsVisibility();
            }
        } else {
            // Original scroll-based slide left
            let slideWidth = this.calculateSlideWidth();
            this.currentPosition = Math.max(this.currentPosition - slideWidth, 0);

            this.feedItems.scrollTo({
                left: this.currentPosition,
                behavior: 'smooth',
            });

            this.updateCurrentIndex();

            if (this.currentPosition == 0) {
                this.leftControl.classList.add(Const.widgetHiddenClass);
            }

            this.rightControl.classList.remove(Const.widgetHiddenClass);
        }
    }

    getCurrentIndex() {
        return this.currentIndex;
    }

    // Handle scrollend event (works for both native scrollend and Safari fallback)
    handleScrollEnd() {
        this.updateCurrentIndex();

        const counterEl = this.container.parentElement.querySelector('.' + Const.widgetFeedItemsCounterClass);
        if (counterEl) {
            if (this.currentIndex === 0) {
                this.counterTextCurr.innerHTML = '1';
            } else {
                this.counterTextCurr.innerHTML = this.currentIndex + 1;
            }
        }
    }

    // New methods for draggable functionality
    handleDragStart(e) {
        if (this.itemElementWidth === 0 || !this.items || this.items.length <= this.numVisibleItems) {
            return;
        }
        
        // Reset drag flag at start - only set to true when actual movement occurs
        this.hasStartedDrag = false;
        e.preventDefault();
        e.stopPropagation();

        this.isDragging = true;
        this.feedItems.style.cursor = 'grabbing';
        this.feedItems.style.transition = 'none';

        this.startX = e.clientX;
        this.dragStartTranslateX = this.currentTranslateX;

        // Add global click blocker
        document.addEventListener('click', this.boundHandleLinkClick, true);

        // Add mousemove and mouseup event listeners
        document.addEventListener('mousemove', this.boundHandleDragMove);
        document.addEventListener('mouseup', this.boundHandleDragEnd);
        document.addEventListener('mouseleave', this.boundHandleDragEnd);
    }

    handleDragMove(e) {
        if (!this.isDragging) return;
        e.preventDefault();
        e.stopPropagation();

        const currentX = e.clientX;
        const diffX = currentX - this.startX;
        
        // Only set drag flag if there's meaningful movement (more than 3 pixels)
        if (Math.abs(diffX) > 3) {
            this.hasStartedDrag = true;
        }
        
        this.feedItems.style.transform = `translateX(${this.dragStartTranslateX + diffX}px)`;
    }

    handleDragEnd(e) {
        if (!this.isDragging) return;
        e.preventDefault();
        e.stopPropagation();

        this.isDragging = false;
        this.feedItems.style.cursor = 'grab';
        this.feedItems.style.transition = 'transform 0.5s ease-in-out';

        document.removeEventListener('mousemove', this.boundHandleDragMove);
        document.removeEventListener('mouseup', this.boundHandleDragEnd);
        document.removeEventListener('mouseleave', this.boundHandleDragEnd);

        // Remove global click blocker after a delay to catch any click events
        setTimeout(() => {
            document.removeEventListener('click', this.boundHandleLinkClick, true);
        }, 150);

        // Reset drag flag after a delay to ensure all click events are blocked
        setTimeout(() => {
            this.hasStartedDrag = false;
        }, 200);

        if (this.itemElementWidth === 0 || this.items.length === 0) {
            this.feedItems.style.transform = `translateX(${this.currentTranslateX}px)`;
            this.checkControlsVisibility();
            return;
        }

        const currentX = e.clientX;
        const diffX = currentX - this.startX;
        const finalProposedTranslateX = this.dragStartTranslateX + diffX;
        const itemFullWidth = this.itemElementWidth + this.gap;

        let snappedItemIndex = 0;
        if (itemFullWidth > 0) {
            snappedItemIndex = Math.round(-finalProposedTranslateX / itemFullWidth);
        }

        const maxAllowedStartIndex = Math.max(0, this.items.length - this.numVisibleItems);
        const effectiveMaxIndex = this.items.length < this.numVisibleItems ? 0 : maxAllowedStartIndex;

        this.currentIndex = Math.max(0, Math.min(snappedItemIndex, effectiveMaxIndex));
        
        if (this.items.length > 0 && this.items.length < this.numVisibleItems) {
            this.currentIndex = 0;
        }

        this.currentTranslateX = -this.currentIndex * itemFullWidth;
        this.isAnimating = true;
        this.feedItems.style.transform = `translateX(${this.currentTranslateX}px)`;
        setTimeout(() => { this.isAnimating = false; }, this.animationDuration);

        if (this.counterTextCurr && this.items.length > 0) {
            this.counterTextCurr.innerHTML = Math.floor(this.currentIndex / this.numItemsToSlide) + 1;
        }
        this.checkControlsVisibility();
    }

    handleWheel(e) {
        if (this.isAnimating) return;
        if (!this.items || this.items.length === 0 || this.itemElementWidth === 0) return;

        const deltaX = e.deltaX;
        const deltaY = e.deltaY;

        if (Math.abs(deltaX) > Math.abs(deltaY)) {
            const scrollThreshold = 10;

            if (deltaX > scrollThreshold) {
                if (!(this.currentIndex >= Math.max(0, this.items.length - this.numItemsToSlide) && this.items.length > 0)) {
                    e.preventDefault();
                    this.slideRight();
                }
            } else if (deltaX < -scrollThreshold) {
                if (this.currentIndex > 0) {
                    e.preventDefault();
                    this.slideLeft();
                }
            }
        }
    }

    addLinkClickListeners() {
        // Get all clickable elements within the slider
        const links = this.feedItems.querySelectorAll('a');
        const feedItems = this.feedItems.querySelectorAll('.garderobo-widget-feed-item');
        const containers = this.feedItems.querySelectorAll('[data-look]');
        
        // Add click prevention to links
        links.forEach(link => {
            link.removeEventListener('click', this.boundHandleLinkClick);
            link.addEventListener('click', this.boundHandleLinkClick);
        });
        
        // Add click prevention to feed items
        feedItems.forEach(item => {
            item.removeEventListener('click', this.boundHandleLinkClick);
            item.addEventListener('click', this.boundHandleLinkClick);
        });
        
        // Add click prevention to look containers
        containers.forEach(container => {
            container.removeEventListener('click', this.boundHandleLinkClick);
            container.addEventListener('click', this.boundHandleLinkClick);
        });
        
        // Add click prevention to the main feed items container itself
        this.feedItems.removeEventListener('click', this.boundHandleLinkClick);
        this.feedItems.addEventListener('click', this.boundHandleLinkClick);
    }

    handleLinkClick(e) {
        if (this.hasStartedDrag) {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            return false;
        }
    }
}